/**
 * @file: assets/99-brands-list.js
 * @purpose: Custom element for displaying a brands list
 * @author: 99 Bikes
 * @date: 2024-06-11
 */

import { brandMetaobjectsQuery } from '//99bikes.com.au/cdn/shop/t/354/assets/99-storefrontAPIQueries.js?manual=20250905&v=9616436342800914981783909884';

const storefrontRequestHeaders = {
  'Content-Type': 'application/graphql',
  'X-Shopify-Storefront-Access-Token': window.theme.storefrontAPI.key,
};

class BrandsList extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Cache DOM elements (none for now)
    this.elements = {};
    this.selectors = {
      brandsColumns: '[data-brands-columns]',
    };
    this.initBrandsList();
  }

  async initBrandsList() {
    let hasNextPage = true;
    let endCursor = null;
    let allResults = [];

    while (hasNextPage) {
      const url = `https://${window.Shopify.shop}/api/${window.theme.storefrontAPI.version}/graphql.json`;
      const options = {
        method: 'POST',
        headers: storefrontRequestHeaders,
        body: brandMetaobjectsQuery(hasNextPage, endCursor),
      };
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error('Storefront response was not OK');
        }
        const responseJSON = await response.json();
        const data = responseJSON.data?.metaobjects;
        const pageInfo = data?.pageInfo;
        allResults = allResults.concat(data.edges);
        hasNextPage = pageInfo.hasNextPage;
        endCursor = pageInfo.endCursor;
      } catch (err) {
        window.console.error(err);
        break;
      }
    }

    const newData = allResults.map(brand => {
      const brandObject = {
        title: brand.node.title?.value || null,
        featured: brand.node.featured?.value || null,
        handle: brand.node.handle,
      };

      return brandObject;
    });

    this.renderBrandsList(newData);
  }

  renderBrandsList(brands) {
    //99B 20260218 -PO, adding error catch for brands missing a title
    let sortedBrands = brands.filter(brand => {
      if (!brand.title) {
        console.error(`Brand metaobject - handle '${brand.handle}' - is missing a title, and is not being displayed.`);
        return false;
      }
      return true;
    }).sort((a, b) => a.title?.localeCompare(b.title));

    if (this.dataset.featuredOnly == 'true') {
      sortedBrands = sortedBrands.filter(brand => brand.featured);
    }

    const groupedBrands = sortedBrands.reduce((acc, brand) => {
      const firstChar = brand.title.charAt(0);
      const isNumber = /^\d$/.test(firstChar);
      const sectionKey = isNumber ? '#' : firstChar.toUpperCase();

      if (!acc[sectionKey]) {
        acc[sectionKey] = [];
      }
      acc[sectionKey].push(brand);
      return acc;
    }, {});

    // Sort the sections alphabetically, with # at the beginning
    const sortedSections = Object.keys(groupedBrands).sort((a, b) => {
      if (a === '#') return -1;
      if (b === '#') return 1;
      return a.localeCompare(b);
    });

    const brandsList = this.querySelector(this.selectors.brandsColumns);
    if (!brandsList) return;

    const html = sortedSections
      .map(
        letter => `
          <div class="brands-list-column" data-section="${letter}">
            <h3 class="brands-list__letter-heading">${letter}</h3>
            <div class="brands-list__items">
              ${groupedBrands[letter]
                .map(
                  brand =>
                    `<div class="brands-list__item"><a href="/collections/${brand.handle}" class="brands-list__item-link">${brand.title}</a></div>`
                )
                .join('')}
            </div>
          </div>
        `
      )
      .join('');

    brandsList.innerHTML = html;
  }
}

// Define the custom element
customElements.define('brands-list', BrandsList);
